<template>
  <div class="row">
    <div class="col-sm-12 col-lg picker">
      <v-select
        v-if="!wojSchema.$disabled"
        value-field="text"
        :no-options-text="emptyListMsg"
        :model="wojSchema"
        :values="wojs"
        :disabled="disabled"
        @input="selectedWoj"
      />
    </div>

    <div v-if="!powSchema.$disabled" class="col-sm-12 col-lg picker">
      <v-select
        value-field="text"
        :disabled="disabled || powDisabled"
        :model="powSchema"
        :values="pows"
        @input="selectedPow"
      />
    </div>

    <div v-if="!gmiSchema.$disabled" class="col-sm-12 col-lg picker">
      <v-select
        value-field="text"
        :disabled="disabled || gmiDisabled"
        :model="gmiSchema"
        :values="gmis"
        @input="selectedGmi"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';

import vSelect from './vue-select.vue';
import { TercType } from '../enums/tercType.enum';

// @vuese
// Input na TERC-a
// @group Components
export default {
  name: 'WTercPicker',
  components: { vSelect },
  props: {
    // URL do TERYT API
    url: { type: String, required: false, default: 'https://teryt.api.witkac.pl' },
    // Schema
    model: { type: Object, required: false },
    // Schema województwa
    wojModel: { type: Object, required: false },
    // Schema powiatu
    powModel: { type: Object, required: false },
    // Schema gminy
    gmiModel: { type: Object, required: false },
    // Schema tercu
    tercModel: { type: Object, required: false },
    // Tekst do wyświetlenia w przypadku pustej listy
    emptyListMsg: { type: String, required: false, default: '' },
    // Czy pokazywać błędy
    showError: { type: Boolean, default: true },
    // Czy pobierać pełny terc
    typeTerc: { type: Boolean, default: false },
    // Czy nieaktywny
    disabled: { type: Boolean, default: false }
  },
  data() {
    return { wojs: [], pows: [], gmis: [] };
  },
  computed: {
    powDisabled() {
      return !this.wojSchema.$model;
    },
    gmiDisabled() {
      return !this.powSchema.$model;
    },
    wojSchema() {
      return this.wojModel || this.model.woj;
    },
    powSchema() {
      return this.powModel || this.model.pow;
    },
    gmiSchema() {
      return this.gmiModel || this.model.gmi;
    },
    tercSchema() {
      return this.tercModel || this.model.terc;
    },
    terc() {
      return this.tercSchema.$model;
    },
    woj() {
      return this.terc?.substring(0, 2);
    },
    pow() {
      return this.terc?.substring(2, 4);
    },
    gmi() {
      return this.terc?.substring(4);
    }
  },
  watch: {
    woj: {
      handler(newValue) {
        if (newValue) {
          this.setWojData();
          this.getPowList();
          this.getGmiList();
        }
      },
      immediate: true
    },
    pow: {
      handler(newValue) {
        if (newValue) {
          this.getGmiList();
        }
      },
      immediate: true
    }
  },
  beforeMount() {
    this.getWojList().then(() => this.setWojData());
  },
  methods: {
    // @vuese
    // Ustawia wybrane województwo na podstawie modelu terc
    setWojData() {
      if (this.woj && this.wojs?.length) {
        const selectedWoj = this.wojs.find(woj => woj.value === this.woj);
        this.wojSchema.$setData(selectedWoj?.text);
      }
    },

    // @vuese
    // Ustawia wybrany powiat na podstawie modelu terc
    setPowData() {
      if (this.pow) {
        const selectedPow = this.pows.find(pow => pow.value === this.pow);
        this.powSchema.$setData(selectedPow?.text);
      }
    },

    // @vuese
    // Ustawia wybraną gminę na podstawie modelu terc
    setGmiData() {
      if (this.gmi) {
        const selectedGmi = this.gmis.find(gmi => gmi.value === this.gmi);
        this.gmiSchema.$setData(selectedGmi?.text);
      }
    },

    // @vuese
    // Metoda pobiera listę województw
    getWojList() {
      return axios
        .get(`${this.url}/terc`, { params: { type: TercType.WOJEWODZTWO } })
        .then(res => (this.wojs = res.data.list.map(item => ({ value: item.woj, text: item.name.toLowerCase() }))))
        .catch(error => this.$emit('error', 'Nie udało się pobrać listy województw'));
    },
    // @vuese
    // Metoda pobiera listę powiatów
    getPowList() {
      if (this.woj) {
        return axios
          .get(`${this.url}/terc`, { params: { type: TercType.POWIAT, woj: this.woj } })
          .then(res => {
            this.pows = res.data.list.map(item => ({ value: item.pow, text: item.name }));
            this.setPowData();
          })
          .catch(error => this.$emit('error', 'Nie udało się pobrać listy powiatów'));
      }
    },
    // @vuese
    // Metoda pobiera listę gmin
    getGmiList() {
      if (this.woj && this.pow) {
        return axios
          .get(`${this.url}/terc?&terc=${this.woj + this.pow}&fields=gmi,nameFull,type`, {
            params: { type: TercType.GMINA, woj: this.woj, pow: this.pow }
          })
          .then(res => {
            this.gmis = res.data.list.map(item => ({
              value: this.typeTerc ? item.gmi + item.type : item.gmi,
              text: item.nameFull
            }));
            if (this.gmis.length === 1) {
              const gmi = this.gmis[0];
              this.gmiSchema.$setData(gmi.text);
              this.selectedGmi(gmi);
            } else {
              this.setGmiData();
            }
          })
          .catch(error => this.$emit('error', 'Nie udało się pobrać listy gmin'));
      }
    },
    // @vuese
    // Wyczyszczenie odpowiednich wartosci po wybraniu województwa oraz zapis do schemy
    // @arg `val` - obiekt z nazwą oraz symbolem województwa
    selectedWoj(val) {
      this.powSchema.$setData(null);
      this.gmiSchema.$setData(null);
      this.tercSchema.$setData(val?.value || null);
      this.$emit('update');
    },
    // @vuese
    // Wyczyszczenie odpowiednich wartości po wybraniu powiatu oraz zapis do schemy
    // @arg `val` - obiekt z nazwą oraz symbolem powiatu
    selectedPow(val) {
      this.gmiSchema.$setData(null);
      this.tercSchema.$setData(this.terc.substring(0, 2) + (val?.value || ''));
      this.$emit('update');
    },
    // @vuese
    // Zapis do schemy
    // @arg `val` - obiekt z nazwą oraz symbolem gminy
    selectedGmi(val) {
      this.tercSchema.$setData(this.terc.substring(0, 4) + (val?.value || ''));
      this.$emit('update');
    }
  }
};
</script>
